import { Injectable } from '@angular/core';
import { BaseClass } from 'app/shared/base-class';
import { CoveragesData } from 'app/modules/submission-management/data/coverages.data';
import { PropertyData } from 'app/modules/submission-management/data/property.data';
import { EndorsementsData } from 'app/modules/submission-management/data/endorsements.data';
import { ClaimsData } from 'app/modules/submission-management/data/claims.data';
import { LvRaterDeductibleHUR, LvRaterOtherPerilDeductibleNHR } from 'app/shared/constants/quick-quote.options.constants';
import * as _ from 'lodash';
import { EntityRiskData } from 'app/modules/submission-management/data/entity-risk.data';
import { PolicySummaryData } from 'app/modules/policy-management/data/policy-summary.data';
import { RiskDetailsDTO } from 'app/shared/models/data/dto/quick-quote/risk-details.dto';
import { UWRHelpers } from './uwr-helpers.service';
import { AuthService } from '../../auth.service';

@Injectable({
  providedIn: 'root',
})
export class PolicyUWR extends BaseClass {
  constructor(
    protected coveragesData: CoveragesData,
    protected propertyData: PropertyData,
    protected endorsementsData: EndorsementsData,
    protected claimsData: ClaimsData,
    protected entityRiskData: EntityRiskData,
    public policySummaryData: PolicySummaryData,
    protected uwrHelpers: UWRHelpers,
    protected authService: AuthService
  ) {
    super();
  }

  get parentRiskDetail(): RiskDetailsDTO {
    return this.entityRiskData?.ParentBackUpRiskData;
  }

  policyUWR1(): boolean {
    // CNTSUP-1790 Remove for midterm endorsement
    return false;
  }

  policyUWR2(): boolean {
    // This will not be trigger since distance to coast is disabled in UI can't be edit
    return false;
  }

  policyUWR3(): boolean {
    // Involved update in AOP Coverage page
    const parentAOP = Math.trunc(Number(this.parentRiskDetail?.riskCoverages[0].allOtherPerilsDeductible));
    const aop = Math.trunc(Number(this.coveragesData.coveragesAndDeductiblesForm.get('allOtherPerilsId')?.value));

    return parentAOP !== aop;
  }

  policyUWR4(): boolean {
    const parentYearBuilt = this.parentRiskDetail?.riskProperties[0]?.yearBuilt;
    const parentLimitedWater = this.parentRiskDetail?.riskEndorsements[0]?.isLimitedWaterDamage;
    const yearBuilt = this.propertyData.propertyDetails.get('yearBuilt')?.value;
    const limitedWater = this.endorsementsData.endorsementsGroup.get('limitedWater')?.value;

    return parentYearBuilt !== yearBuilt || parentLimitedWater !== limitedWater;
  }

  policyUWR5(): boolean {
    let isCoverageChanged: boolean = false;

    if (this.uwrHelpers.isHO3orDP3) {
      const parentCoverageA = Math.trunc(Number(this.parentRiskDetail?.riskCoverages[0]?.covA));
      const coverageA = Math.trunc(Number(this.coveragesData.coveragesAndDeductiblesForm.get('coverageA')?.value));
      isCoverageChanged = parentCoverageA !== coverageA;

    } else {
      isCoverageChanged = false;
    }

    const parentHurricaneDeductible = this.parentRiskDetail?.riskCoverages[0]?.hurricaneDeductible;
    const hurricaneDeductible = this.coveragesData.coveragesAndDeductiblesForm.get('hurricaneDeductibleid')?.value;
    const isHurDeductibleChanged: boolean = parentHurricaneDeductible !== hurricaneDeductible;

    return this.uwrHelpers.isHO3orDP3 && (isCoverageChanged || isHurDeductibleChanged);
  }

  policyUWR9(): boolean {
    // Invovled Update in:
    //  yearBuit > Property page
    //  waterDamage > Endorsement page
    const parentYearBuilt = this.parentRiskDetail?.riskProperties[0]?.yearBuilt;
    const parentWaterDamage = this.parentRiskDetail?.riskEndorsements[0]?.isWaterDamageExclusion ?? false;
    const yearBuilt = Math.trunc(Number(this.propertyData.propertyDetails.get('yearBuilt')?.value));
    const waterDamage = this.endorsementsData.endorsementsGroup.get('waterDamage')?.value;

    return parentYearBuilt !== yearBuilt || parentWaterDamage !== waterDamage;
  }

  policyUWR14(): boolean {
    // Related on Flood assist API not yet implemented.
    // Involve Update in:
    //    floodCoverage > Coverage Page > Flood Section
    //    lowestFloorElevattion > Coverage Page > Flood Section
    //    baseFlood > Coverage Page > Flood Section
    const originalFloodCoverage = this.parentRiskDetail?.riskFloods[0]?.isPrivateFloodCov;
    const parentlowestFloorElevation = Math.trunc(Number(this.parentRiskDetail?.riskFloods[0]?.lowestFloorElevation));
    const parentbaseFlood = Math.trunc(Number(this.parentRiskDetail?.riskFloods[0]?.baseFloodElevation));
    const floodCoverage = this.coveragesData.floodForm.get('privateFloodCoverage')?.value;
    const lowestFloorElevation = +this.coveragesData.floodForm.get('lowestFloodElevation')?.value;
    const baseFlood = +this.coveragesData.floodForm.get('baseFloodElevation')?.value;
    return (originalFloodCoverage !== floodCoverage) ||
      (parentlowestFloorElevation !== lowestFloorElevation) ||
      (parentbaseFlood !== baseFlood);
  }

  policyUWR15(): boolean {
    // Input Update in:
    //    floodCoverage > Coverage Page > Flood Section
    //    floodLossAtThisLocation > Coverage Page > Flood Section
    //    claimList where claimType === flood

    const parentFloodCoverage = this.parentRiskDetail?.riskFloods[0]?.isPrivateFloodCov;
    const floodCoverage = this.coveragesData.floodForm.get('privateFloodCoverage')?.value;
    let hasNewClaim = false;

    this.claimsData.claimList.forEach(item => {
      if (!item.isFromParent && item.claimType === 'FLOOD' && item.isAPlus) {
        hasNewClaim = true;
        return false;
      }
    });

    return (parentFloodCoverage !== floodCoverage) || hasNewClaim;
  }

  policyUWR16(): boolean {
    // Involve Update in
    //    propertyUsage > Property Page > Location Section
    //    structureType > Property Page > Location Section
    const parentPropertyUsage = this.parentRiskDetail?.riskProperties[0]?.propertyUsageCode;
    const parentStructureType = this.parentRiskDetail?.riskProperties[0]?.structureTypeCode;

    const propertyUsage = this.propertyData.locationDetails.get('propertyUsage')?.value;
    const structureType = this.propertyData.locationDetails.get('structureType')?.value;

    return parentPropertyUsage !== propertyUsage || parentStructureType !== structureType;
  }

  policyUWR17(): boolean {
    // Involve Updates in:
    //    floodZone > Coverage Page > Flood Section
    //    applicantFloodPolicyId > Coverage Page > Flood Section
    const parentFloodZone = this.parentRiskDetail?.riskFloods[0].floodZone;
    const parentApplicantFloodPolicyId = this.parentRiskDetail?.riskFloods[0]?.applicantFloodPolicyCode;
    const parentFloorUnitNumber = this.parentRiskDetail?.riskProperties[0]?.floorUnitNumber;

    const floodZone = this.coveragesData.floodForm.get('floodZone')?.value;
    const applicantFloodPolicyId = this.coveragesData.floodForm.get('applicantFloodPolicyId')?.value;
    const floorUnitNumber = this.propertyData.propertyDetails.get('floorUnitNumber').value;

    return parentFloodZone !== floodZone || parentApplicantFloodPolicyId !== applicantFloodPolicyId || parentFloorUnitNumber !== floorUnitNumber;
  }

  policyUWR18(): boolean {
    // TODO: Agent Cancel/Rewrite
    // Involve Updates in:
    //    yearBuilt > Property Page > Property Details Section
    //    waterDamage > Endorsement Page
    const parentYearBuilt = this.parentRiskDetail?.riskProperties[0]?.yearBuilt;
    const parentWaterDamage = this.parentRiskDetail?.riskEndorsements[0]?.isWaterDamageExclusion;

    const yearBuilt = this.propertyData.propertyDetails.get('yearBuilt')?.value;
    const waterDamage = this.endorsementsData.endorsementsGroup.get('waterDamage')?.value;

    return parentYearBuilt !== yearBuilt || parentWaterDamage !== waterDamage ;
  }

  policyUWR21(): boolean {
    let parentCentralAir = false;
    if (!this.parentRiskDetail?.riskProperties[0]?.isCentralAirCondition){
      parentCentralAir = true;
    } else {
      parentCentralAir = false;
    };

    let centralAirFalseInUwOrProperty = false;
    if(!this.propertyData.propertyDetails.get('isCentralAirConditioning')?.value) {
      centralAirFalseInUwOrProperty = true;
    } else {
      centralAirFalseInUwOrProperty = false;
    }

    return centralAirFalseInUwOrProperty !== parentCentralAir;
  }

  policyUWR22(): boolean {
    const parentCoverageA = Math.trunc(Number(this.parentRiskDetail?.riskCoverages[0]?.covA));
    const parentReplacementCost = Math.trunc(Number(this.parentRiskDetail?.riskProperties[0]?.replacementCost));

    const coverageA = Math.trunc(Number(this.coveragesData.coveragesAndDeductiblesForm.get('coverageA')?.value));
    const replacementCost = Math.trunc(Number(this.propertyData.propertyDetails.get('replacementCost')?.value));

    return parentCoverageA !== coverageA || parentReplacementCost !== replacementCost;
  }

  policyUWR23(): boolean {
    // Involve Update
    //    floodZone > Coverage Page > Flood Section
    //    applicantFloodPolicyId > Coverage Page > Flood Section
    const parentFloodZone = this.parentRiskDetail?.riskFloods[0].floodZone;
    const parentApplicantFloodPolicyId = this.parentRiskDetail?.riskFloods[0]?.applicantFloodPolicyCode;

    const floodZone = this.coveragesData.floodForm.get('floodZone')?.value;
    const applicantFloodPolicyId = this.coveragesData.floodForm.get('applicantFloodPolicyId')?.value;

    return parentFloodZone !== floodZone || parentApplicantFloodPolicyId !== applicantFloodPolicyId;
  }

  policyUWR25(): boolean {
    // TODO: Pending Clarification CENTAURI-1323 related in Submission UWR CENTAURI-190
    // As per discussion in CENTAURI-1323, UWR-25 should be implemented but should not be triggered.

    // const parentStarPackage = this.parentRiskDetail?.riskEndorsements[0]?.isStarPackage;
    // const parentAdvantagePackage = this.parentRiskDetail?.riskEndorsements[0]?.isStarAdvantagePackage;
    // const parentClaimList = this.parentRiskDetail?.riskClaims.length;
    // const parentClaimItems = [];
    // this.parentRiskDetail?.riskClaims.filter(claim => claim.isActive === true).map(item => {
    //   parentClaimItems.push(item.claimType);
    // });

    // const starPackage = this.endorsementsData.endorsementsGroup.get('starPackage')?.value;
    // const starAdvantagePackage = this.endorsementsData.endorsementsGroup.get('starAdvantagePackage')?.value;
    // const claimList = this.claimsData.claimList.length;
    // const claimItems = [];
    // this.claimsData.claimList.filter(claim => claim.isActive === true).map(item => {
    //   claimItems.push(item.claimType);
    // });

    // const hasChangeOnClaims = JSON.stringify(parentClaimItems) !== JSON.stringify(claimItems);

    // return (parentStarPackage !== starPackage || parentAdvantagePackage !== starAdvantagePackage) || parentClaimList !== claimList || hasChangeOnClaims;
    return false;
  }

  policyUWR26(): boolean {
    // Involve updates in:
    //  numMonthsOccupied > Property Page > Location Section
    //  propertyUsage > Property Page > Location Section
    const parentNumMonthsOccupied = this.parentRiskDetail?.riskProperties[0]?.numberMonthOccupied;
    const parentPropertyUsage = this.parentRiskDetail?.riskProperties[0]?.propertyUsageCode;

    const numMonthsOccupied = this.propertyData.locationDetails.get('numMonthsOccupied')?.value;
    const propertyUsage = this.propertyData.locationDetails.get('propertyUsage')?.value;

    return parentNumMonthsOccupied !== numMonthsOccupied || parentPropertyUsage !== propertyUsage;
  }

  policyUWR27(): boolean {
    // Involve updates in:
    //    propertyUsage > Property page > Location Section
    const parentPropertyUsage = this.parentRiskDetail?.riskProperties[0].propertyUsageCode;
    const propertyUsage = this.propertyData.locationDetails.get('propertyUsage')?.value;

    return parentPropertyUsage !== propertyUsage;
  }

  policyUWR30(): boolean {
    // Involve update in:
    //    electrical > Property Page > Property Details Section
    const parentElectrical = this.parentRiskDetail?.riskProperties[0]?.electricalCode;
    const electrical = this.propertyData.propertyDetails.get('electrical')?.value;

    return parentElectrical !== electrical;
  }

  policyUWR37(): boolean {
    // Involve update in:
    //    roofMaterial > Property Page > Property Details Section\
    const parentRoofMaterial = this.parentRiskDetail?.riskProperties[0]?.roofMaterialCode;
    const roofMaterial = this.propertyData.propertyDetails.get('roofMaterial')?.value;

    return parentRoofMaterial !== roofMaterial;
  }

  policyUWR39(): boolean {
    const parentFeetToHydrant = this.parentRiskDetail?.riskProperties[0]?.feetToHyrantCode;
    const feetToHydrant = this.propertyData.locationDetails.get('feetToHydrant')
      ?.value;
      return this.uwrHelpers.isDP3 && feetToHydrant !== parentFeetToHydrant;
  }

  policyUWR40(): boolean {
    // Involve update in:
    //    roofShape > Property Page > Property Details Section
    const parentRoofShape = this.parentRiskDetail?.riskProperties[0]?.raterRoofShapeCode;
    const parentRoofMaterial = this.parentRiskDetail?.riskProperties[0]?.roofMaterialCode;
    const roofShape = this.propertyData.propertyDetails.get('roofShape')?.value;
    const roofMaterial = this.propertyData.propertyDetails.get('roofMaterial')?.value;

    return parentRoofShape !== roofShape || parentRoofMaterial !== roofMaterial;
  }

  policyUWR42(): boolean {
    //  foundationType > Property Page > Property Details Section
    //  foundationElevation > Property Page > Property Details Section
    const parentFoundationType = this.parentRiskDetail?.riskProperties[0]?.raterFoundationTypeCode;
    const parentFoundationType360Code = this.parentRiskDetail?.riskProperties[0]?.foundationType360Code;
    const parentFoundationElevation = this.parentRiskDetail?.riskProperties[0].foundationElevationCode;

    const foundationType = this.propertyData.propertyDetails.get('foundationType')?.value;
    const foundationElevation = this.propertyData.propertyDetails.get('foundationElevation')?.value;

    const foundationTypeStatus = !(foundationType === parentFoundationType || foundationType === parentFoundationType360Code);

    return foundationTypeStatus || parentFoundationElevation !== foundationElevation;
  }

  policyUWR43(): boolean {
    // Involve update in:
    //    electrical > Property Page > Property Details Section
    const parentElectrical = this.parentRiskDetail?.riskProperties[0].electricalCode;
    const electrical = this.propertyData.propertyDetails.get('electrical')?.value;
    return parentElectrical !== electrical;
  }

  policyUWR47(): boolean {
    // Involve update in:
    //    electrical > Property Page > Property Details Section
    const parentElectrical = this.parentRiskDetail?.riskProperties[0].electricalCode;
    const electrical = this.propertyData.propertyDetails.get('electrical')?.value;
    return parentElectrical !== electrical;
  }

  policyUWR58(): boolean {
    // Involve update in:
    //    primaryHeatType > Property Page > Property Detail Section
    const parentPrimaryHeatType = this.parentRiskDetail?.riskProperties[0]?.primaryHeatTypeCode;
    const primaryHeatType = this.propertyData.propertyDetails.get('primaryHeatType')?.value;
    return parentPrimaryHeatType !== primaryHeatType;
  }

  policyUWR68(): boolean {
    // Involve update in:
    //    roofMaterial > Property Page > Property Detail Section
    //    yearRoof > Property Page > Property Detail Section
    const parentRoofMaterial = this.parentRiskDetail?.riskProperties[0]?.roofMaterialCode;
    const parentYearRoof = this.parentRiskDetail?.riskProperties[0]?.yearRoofInstalled;

    const roofMaterial = this.propertyData.propertyDetails.get('roofMaterial').value;
    const yearRoof = this.propertyData.propertyDetails.get('yearRoof')?.value;

    return parentRoofMaterial !== roofMaterial ||
      Math.trunc(Number(parentYearRoof)) !== Math.trunc(Number(yearRoof));
  }

  policyUWR69(): boolean {
    // Involve update in:
    //    roofMaterial > Property Page > Property Detail Section
    //    yearRoof > Property Page > Property Detail Section
    const parentRoofMaterial = this.parentRiskDetail?.riskProperties[0]?.roofMaterialCode;
    const parentYearRoof = this.parentRiskDetail?.riskProperties[0]?.yearRoofInstalled;

    const roofMaterial = this.propertyData.propertyDetails.get('roofMaterial').value;
    const yearRoof = this.propertyData.propertyDetails.get('yearRoof')?.value;

    return parentRoofMaterial !== roofMaterial ||
      Math.trunc(Number(parentYearRoof)) !== Math.trunc(Number(yearRoof));
  }

  policyUWR75(): boolean {
    // Involve update in:
    //    roofMaterial > Property Page > Property Detail Section
    const parentRoofMaterial = this.parentRiskDetail?.riskProperties[0]?.roofMaterialCode;

    const roofMaterial = this.propertyData.propertyDetails.get('roofMaterial').value;

    return parentRoofMaterial !== roofMaterial;
  }

  policyUWR77(): boolean {
    //    HO3 and Optional endorsements "Ordinance or Law Coverage - 25%" OR "Ordinance or Law Coverage - 50%" are not included (default to add 25% to policies if they remove, fire rule).  If they have no O&L options, trigger the referral unless they have either Star Package or Star Advantage Package selected.
    const parentOrdinanceLawOrCov = this.parentRiskDetail.riskEndorsements[0].isOrdinanceLaw ?? false;
    const parentStarPackage = this.parentRiskDetail.riskEndorsements[0].isStarPackage ?? false;
    const parentStarAdvPackage = this.parentRiskDetail.riskEndorsements[0].isStarAdvantagePackage ?? false;

    const ordinanceLawOrCov = this.endorsementsData.endorsementsGroup.get('ordinanceLawOrCov')?.value;
    const starPackage = this.endorsementsData.endorsementsGroup.get('starPackage').value || false;
    const starAdvantagePackage = this.endorsementsData.endorsementsGroup.get('starAdvantagePackage')?.value || false;

    return parentOrdinanceLawOrCov !== ordinanceLawOrCov ||
                    parentStarPackage !== starPackage ||
                    parentStarAdvPackage !== starAdvantagePackage;
  }

  policyUWR82(): boolean {
    const status = this.uwrHelpers.asyncUWRs.find(uwr => { return uwr.id === 82; }).status;

    return status;
  }

  policyUWR85(): boolean {
    const status = this.uwrHelpers.asyncUWRs.find(uwr => { return uwr.id === 85; }).status;

    return status;
  }

  policyUWR86(): void {
    // TEMPORARILY Comment this code, as per business UWR86: turn this off until the copy feature is turned back on

    // const parentPriorInsuranceCarrier = this?.parentRiskDetail?.riskInterests[0]?.priorCarrierCode;
    // const priorInsuranceCarrier = this?.interestData?.priorInsuranceForm?.get('priorCarrier')?.value;
    // const status = parentPriorInsuranceCarrier !== priorInsuranceCarrier;

    // if (status) {
    //   this.uwr86();
    // } else {
    //   this.policyRemoveFromTableRow('uwr86');
    // }
  }

  policyUWR87(): boolean {
    //    HO3 and Coverage A limit is greater than $1,000,000.
    const parentCovA = Math.trunc(Number(this.parentRiskDetail.riskCoverages[0].covA));
    const coverageA = Math.trunc(Number(this.coveragesData.coveragesAndDeductiblesForm.get('coverageA')?.value));

    return parentCovA !== coverageA;
  }

  policyUWR88(): boolean {

    // Involve update in:
    //    foundationType > Property Page > Property Detail Section
    //    foundationElevation > Property Page > Property Detail Section
    const parentFoundationType: string = this.parentRiskDetail?.riskProperties[0]?.raterFoundationTypeCode ?? '';
    const parentFoundationElevation: string = this.parentRiskDetail?.riskProperties[0]?.foundationElevationCode ?? '';

    const foundationType: string = this.propertyData.propertyDetails.get('foundationType')?.value ?? '';
    const foundationElevation: string = this.propertyData.propertyDetails.get('foundationElevation')?.value ?? '';

    const deepPillingCode: string = 'DP';

    if (parentFoundationType === foundationType && parentFoundationType === deepPillingCode) {
      return parentFoundationElevation !== foundationElevation;
    }

    return parentFoundationType !== foundationType;
  }

  policyUWR89(): boolean {
    // Involve update in:
    //    propertyUsage > Property Page > Location Detail Section
    //    structureType > Property Page > Location Detail Section
    const parentPropertyUsage = this.parentRiskDetail?.riskProperties[0]?.propertyUsageCode;
    const parentStructureType = this.parentRiskDetail?.riskProperties[0]?.structureTypeCode;

    const propertyUsage = this.propertyData.locationDetails.get('propertyUsage')?.value;
    const structureType = this.propertyData.locationDetails.get('structureType')?.value;

    return parentPropertyUsage !== propertyUsage ||
      parentStructureType !== structureType;
  }

  policyUWR90(): boolean {
    // Involve update in:
    //    limitedFungi > Endorsement Page
    //    limitedFungiLimit > Endorsement Page
    let parentLimitedFungi = this.parentRiskDetail?.riskEndorsements[0]?.isLimitedFungiWet;
    let parentLimitedFungiLimit = Math.trunc(Number(this.parentRiskDetail?.riskEndorsements[0]?.limitedFungiWetLimitCode));

    let limitedFungi = this.endorsementsData.endorsementsGroup.get('limitedFungi')?.value;
    let limitedFungiLimit = this.endorsementsData.endorsementsGroup.get('limitedFungiLimit')?.value;

    if(this.uwrHelpers.isDP3){
      parentLimitedFungi = this.parentRiskDetail?.riskEndorsements[0]?.isLimitedFungiWetIncreasedAmount;
      parentLimitedFungiLimit = Math.trunc(Number(this.parentRiskDetail?.riskEndorsements[0]?.limitedFungiWetIncreasedAmountLimitCode));
      limitedFungi = this.endorsementsData.endorsementsGroup.get('fungiWetOrDryRot')?.value;
      limitedFungiLimit = this.endorsementsData.endorsementsGroup.get('fungiWetOrDryRotLimit')?.value;
    }

    return parentLimitedFungi !== limitedFungi ||
      parentLimitedFungiLimit !== Math.trunc(Number(limitedFungiLimit));
  }

  policyUWR94(): boolean {
    // TODO: Table value comparison
    // Involves:
    // 1. Coverages > Coverages > Coverages > Coverage A - Dwelling
    // 2. Coverages > Schedules > Add Schedules
    // 3. Coverages > Schedules > Schedule Total (Note a field but a value)
    const parentData = this.parentRiskDetail;
    return this.coveragesData.policyCompareRiskScheduleTableData(parentData);
  }

  policyUWR95(): boolean {
    // Involves:
    // 1. Coverages > Coverages > Coverages > Coverage C
    // 2. Coverages > Schedules > Add Schedules
    // 3. Coverages > Schedules > Schedule Total (Note a field but a value)
    const parentCovC = Math.trunc(Number(this.parentRiskDetail?.riskCoverages[0].covC));
    const parentAddSched = this.parentRiskDetail?.isAddSchedule;

    const covC = Math.trunc(this.coveragesData.coveragesAndDeductiblesForm.get('coverageC')?.value);
    const addSchedules = this.coveragesData.schedulesForm.get('addSchedules')?.value;

    const parentData = this.parentRiskDetail;
    const isTableUpdate = this.coveragesData.policyCompareRiskScheduleTableData(parentData);

    return isTableUpdate || (parentCovC !== covC) || (parentAddSched !== addSchedules);
  }

  policyUWR96(): boolean {
    // Involves:
    // 1.  sinkholeLoss > Endorsement Page
    const parentSinkholes = this.parentRiskDetail?.riskEndorsements[0]?.isSinkHoleLossCov;
    const sinkholeLoss = this.endorsementsData.endorsementsGroup.get('sinkholeLoss')?.value;

    return parentSinkholes !== sinkholeLoss;
  }

  policyUWR100(): boolean {
    const originalFloodCoverage = this.parentRiskDetail?.riskFloods[0]?.isPrivateFloodCov;
    const floodCoverage = this.coveragesData.floodForm.get('privateFloodCoverage')?.value;
    return originalFloodCoverage !== floodCoverage;
  }

  policyUWR102(): boolean {
    // TODO: Flood assis API related to CENTAURI-267
    const originalFloodCoverage = this.parentRiskDetail?.riskFloods[0]?.isPrivateFloodCov;
    const floodCoverage = this.coveragesData.floodForm.get('privateFloodCoverage')?.value;
    return originalFloodCoverage !== floodCoverage;
  }

  policyUWR103(): boolean {
    // Involve update in:
    //    exteriorWallFinish > Property Page > Property Detail Section
    const parentExteriorWallFinish = this.parentRiskDetail?.riskProperties[0]?.raterExteriorWallFinishCode;
    const exteriorWallFinish = this.propertyData.propertyDetails.get('exteriorWallFinish')?.value;

    return parentExteriorWallFinish !== exteriorWallFinish;
  }

  policyUWR106(): boolean {
    // Involves:
    // Policy Issue > Change Effective Date
    // Policy Effective Date
    // this.policyIssueData.issuePolicyForm.get('changeEffectiveDate').valueChanges.pipe(takeUntil(this.stop$)).subscribe(value => {
    //   this.checkUWR106();
    // });
    return false;
  }

  policyUWR107(): boolean {
    // Involves:
    // 1. Coverages > Coverages > Coverages > Coverage C
    const parentCovC = this.parentRiskDetail?.riskCoverages[0].covC;
    const covC = Math.trunc(Number(this.coveragesData.coveragesAndDeductiblesForm.get('coverageC')?.value) ?? 0) ;

    return parentCovC !== covC;
  }

  policyUWR109(): boolean {
    const parentCovA = Math.trunc(Number(this.parentRiskDetail?.riskCoverages[0].covA));
    const covA = Math.trunc(Number(this.coveragesData.coveragesAndDeductiblesForm.get('coverageA')?.value));

    return parentCovA !== covA;
  }

  policyUWR114() {
    // For DP3 Only
    // Involve
    // Property > County
    // Property > Yearbuilt
    // Endorsement > Limited Water Damage Coverage
    const parentYearBuilt = Math.trunc(Number(this.parentRiskDetail?.riskProperties[0]?.yearBuilt));
    const parentCounty = this.parentRiskDetail?.riskProperties[0]?.county;
    const parentLimitedWater = this.parentRiskDetail?.riskEndorsements[0]?.isLimitedWaterDamage || false;

    const yearBuilt = Math.trunc(Number(this.propertyData.propertyDetails.get('yearBuilt')?.value));
    const county = this.uwrHelpers.stringToLower(this.propertyData.locationDetails.get('county')?.value);
    const limitedWater = this.endorsementsData.endorsementsGroup.get('limitedWater')?.value || false;

    return parentYearBuilt !== yearBuilt ||
        this.uwrHelpers.stringToLower(parentCounty) !== this.uwrHelpers.stringToLower(county) ||
        parentLimitedWater !== limitedWater;
  }

  policyUWR115(): boolean {
    // Involve Updates in:
    //    yearBuilt > Property Page > Property Details Section
    //    waterDamage > Endorsement Page
    const parentYearBuilt = this.parentRiskDetail?.riskProperties[0]?.yearBuilt;
    const parentWaterDamage = this.parentRiskDetail?.riskEndorsements[0]?.isWaterDamageExclusion;

    const yearBuilt = this.propertyData.propertyDetails.get('yearBuilt')?.value;
    const waterDamage = this.endorsementsData.endorsementsGroup.get('waterDamage')?.value;

    return parentYearBuilt !== yearBuilt || parentWaterDamage !== waterDamage;
  }

  policyUWR116(): boolean {
    // For DP3 Only
    // Involved update in Coverage A value
    const parentCovA = Math.trunc(Number(this.parentRiskDetail?.riskCoverages[0]?.covA));
    const covA = Math.trunc(Number(this.coveragesData.coveragesAndDeductiblesForm.get('coverageA')?.value));

    return parentCovA !== covA;
  }

  policyUWR117(): boolean {
    // FOR Dp3 Only
    // Involve
    // Endorsement > Permitted Incidental Occupancies
    // Endorsement > Permitted Incidental Occupancies - Liability
    // Condition > If agent adds "Permitted Incidental Occupancies" and/or "Permitted Incidental Occupancies - Liability" optional endorsement(s)

    const parentPermittedIncidenal = this.parentRiskDetail?.riskEndorsements[0].isPermittedIncidentalOccupancies || false;
    const parentPermittedIncidentalOccupanciesLiability = this.parentRiskDetail?.riskEndorsements[0].isPermittedIncidentalOccupanciesLiability || false;
    const parentOccupancyDescription = this.uwrHelpers.stringToLower(this.parentRiskDetail?.riskEndorsements[0]?.occupancyDescription);
    const parentBusinessDescription = this.uwrHelpers.stringToLower(this.parentRiskDetail?.riskEndorsements[0]?.businessDescription);
    const parentBusinessLocation = this.uwrHelpers.stringToLower(this.parentRiskDetail?.riskEndorsements[0]?.businessLocation);

    const permittedIncidenal = this.endorsementsData.endorsementsGroup.get('permittedIncidentalOccupancies')?.value || false;
    const occupancyDescription = this.uwrHelpers.stringToLower(this.endorsementsData.endorsementsGroup.get('descriptionOfOccupancy')?.value);
    const permittedIncidentalOccupanciesLiability = this.endorsementsData.endorsementsGroup.get('permittedIncidentalOccupanciesLiability')?.value || false;
    const businessDescription = this.uwrHelpers.stringToLower(this.endorsementsData.endorsementsGroup.get('descriptionOfBusiness')?.value);
    const businessLocation = this.uwrHelpers.stringToLower(this.endorsementsData.endorsementsGroup.get('businessLocation')?.value);

    return parentPermittedIncidenal !== permittedIncidenal ||
      parentPermittedIncidentalOccupanciesLiability !== permittedIncidentalOccupanciesLiability ||
      parentOccupancyDescription !== occupancyDescription ||
      parentBusinessDescription !== businessDescription ||
      parentBusinessLocation !== businessLocation;
  }

  policyUWR118(): boolean {
    const parentMitigationForm = this.parentRiskDetail?.riskProperties[0].mitigationFormCode;
    const mitigationForm = this.propertyData.windMitigation.get('mitigationForm')?.value;
    return this.uwrHelpers.isHO3orDP3orHO6 && parentMitigationForm !== mitigationForm;
  }

  policyUWR120(): boolean {
    const parentSprinkler = this.parentRiskDetail?.riskDiscounts[0]?.sprinklerCode;
    const sprinkler = this.coveragesData.discountsForm.get('sprinklerId')?.value;
    return this.uwrHelpers.isHO3orDP3orHO6 && parentSprinkler !== sprinkler;
  }

  policyUWR121(): boolean {
    const parentHurricaneDeductible = this.parentRiskDetail?.riskCoverages[0]?.hurricaneDeductible;
    const hurricaneDeductible = this.coveragesData.coveragesAndDeductiblesForm.get('hurricaneDeductibleid')?.value;
    const baseUWR121UpdateEffectiveDate = new Date(this.authService.uwr121UpdateEffectiveDate);
    const quoteEffectiveDate = new Date(this.uwrHelpers.riskEffectiveDate);

    const status = (this.uwrHelpers.isHO3orDP3 && parentHurricaneDeductible !== hurricaneDeductible) ||
    (this.uwrHelpers.isHO4orHO6 && parentHurricaneDeductible !== hurricaneDeductible && quoteEffectiveDate >= baseUWR121UpdateEffectiveDate);

    return status;
  }

  policyUWR122(): boolean {
    const parentCovC = Math.trunc(Number(this.parentRiskDetail?.riskCoverages[0]?.covCPercentageId));
    const parentCoverageC = Math.trunc(Number(this.parentRiskDetail?.riskCoverages[0]?.covC));
    const covC = Math.trunc(Number(this.coveragesData.coveragesAndDeductiblesForm.get('coverageCLimitId')?.value));
    const coverageC = Math.trunc(Number(this.coveragesData.coveragesAndDeductiblesForm.get('coverageC')?.value));
    let status: boolean = false;

    if (this.uwrHelpers.isHO6orDP3) {
      status = this.uwrHelpers.isHO6orDP3 && parentCoverageC !== coverageC;
    } else {
      status = this.uwrHelpers.isHO3 && parentCovC !== covC;
    }

    return status;
  }

  policyUWR123(): boolean {
    // TODO: Identify UWR 123
    return false;
  }

  policyUWR124(): boolean {
    // If midterm increase to Coverage A is 20% or more of Coverage A.
    const parentCovA = Math.trunc(Number(this.parentRiskDetail.riskCoverages[0].covA));
    const currentCovA = Math.trunc(Number(this.coveragesData.coveragesAndDeductiblesForm.get('coverageA')?.value));

    const parentCovA20Percent = parentCovA * 0.2;

    return currentCovA >= Math.trunc(Number((parentCovA + parentCovA20Percent)));
  }

  policyUWR125(): boolean {
    // If midterm decrease to Coverage A (any decrease).
    const parentCovA = Math.round(Number(this.parentRiskDetail.riskCoverages[0].covA));
    const currentCovA = Math.round(Number(this.coveragesData.coveragesAndDeductiblesForm.get('coverageA')?.value));

    return currentCovA < parentCovA;
  }

  policyUWR127(): boolean {
    // If any deductible (AOP, wind/hail, hurricane, non-hurricane wind) is lowered.

    // Cov A
    const parentCovA: number = Math.trunc(Number(this.parentRiskDetail.riskCoverages[0].covA));
    const currentCovA: number = Math.trunc(Number(this.coveragesData.coveragesAndDeductiblesForm.get('coverageA')?.value));

    // AOP
    const parentAOP: number = Math.trunc(Number(this.parentRiskDetail.riskCoverages[0].allOtherPerilsDeductible));
    const currentAOP: number = Math.trunc(Number(this.coveragesData.coveragesAndDeductiblesForm.get('allOtherPerilsId')?.value));

    // Hurricane Deductible
    const parentHurricaneDeductibleId: string = this.parentRiskDetail.riskCoverages[0]?.hurricaneDeductible;
    const parentHurricaneDeductible = LvRaterDeductibleHUR.filter(a => a.code === parentHurricaneDeductibleId)[0];
    let parentHurricaneDeductibleValue: number;

    const currentHurricaneDeductibleId: string = this.coveragesData.coveragesAndDeductiblesForm.get('hurricaneDeductibleid')?.value;
    const currentHurricaneDeductible = LvRaterDeductibleHUR.find(a => a.code === currentHurricaneDeductibleId);
    let currentHurricaneDeductibleValue: number;
    let currentHurricaneDeductibleIsExcluded: boolean = false;

    // Changes Checking
    let isHurricaneDeductibleLowered: boolean;
    let isAOPLowered: boolean;

    if (parentHurricaneDeductible) {
      if (parentHurricaneDeductibleId !== 'E') {
        if (parentHurricaneDeductible?.isPercentage) {
          parentHurricaneDeductibleValue = (parentCovA * Math.trunc(Number(parentHurricaneDeductibleId))) / 100;
        } else {
          parentHurricaneDeductibleValue = Math.trunc(Number(parentHurricaneDeductible.code))
        }
      } else {
        parentHurricaneDeductibleValue = 0;
      }
    }

    if (currentHurricaneDeductible) {
      if (currentHurricaneDeductibleId !== 'E') {
        if (currentHurricaneDeductible?.isPercentage) {
          currentHurricaneDeductibleValue = (currentCovA * Math.trunc(Number(currentHurricaneDeductibleId))) / 100;
        } else {
          currentHurricaneDeductibleValue = Math.trunc(Number(currentHurricaneDeductible.code));
        }
      } else {
        currentHurricaneDeductibleIsExcluded = true;
      }
    }

    isAOPLowered = currentAOP < parentAOP;
    isHurricaneDeductibleLowered = currentHurricaneDeductibleIsExcluded ? false :
      currentHurricaneDeductibleValue < parentHurricaneDeductibleValue;

    return isAOPLowered || isHurricaneDeductibleLowered;
  }

  policyUWR128(): boolean {
    // If wind was excluded voluntarily (not per UWR-2) and agent attempts to add wind back to policy.
    const status = this.uwrHelpers.asyncUWRs.find(uwr => { return uwr.id === 128; }).status;

    return status;
  }

  policyUWR129(): boolean {
    // If agent processes change to named insured.
    const status = this.uwrHelpers.asyncUWRs.find(uwr => { return uwr.id === 129; }).status;

    return status;
  }

  policyUWR131(): boolean {
    const status = this.uwrHelpers.asyncUWRs.find(uwr => { return uwr.id === 131; }).status;

    return status;
  }

  policyUWR132(): boolean {
    // Involves changes in:
    // Property:
    //    Property Usage
    //    Is the residence Rented?
    // UW Question: UW Question is not editable in midterm endorsement.
    const parentPropertyUsageCode = this.parentRiskDetail?.riskProperties[0].propertyUsageCode;
    const parentIsResidenceRented = this.parentRiskDetail?.riskProperties[0].isTheResidenceRented;

    const currentPropertyUsageCode = this.propertyData.locationDetails.get('propertyUsage').value;
    const currentIsResidenceRented = this.propertyData.locationDetails.get('isResidenceRented')?.value ?? false;

    return parentPropertyUsageCode !== currentPropertyUsageCode ||
                  parentIsResidenceRented !== currentIsResidenceRented;
  }

  policyUWR133(): boolean {
    // Involves changes in:
    // Property:
    //    Property Usage
    //    Is the residence Rented?
    // UW Question: UW Question is not editable in midterm endorsement.
    const parentPropertyUsageCode = this.parentRiskDetail?.riskProperties[0].propertyUsageCode;
    const parentIsResidenceRented = this.parentRiskDetail?.riskProperties[0].isTheResidenceRented;

    const currentPropertyUsageCode = this.propertyData.locationDetails.get('propertyUsage').value;
    const currentIsResidenceRented = this.propertyData.locationDetails.get('isResidenceRented')?.value ?? false;

    return parentPropertyUsageCode !== currentPropertyUsageCode ||
                  parentIsResidenceRented !== currentIsResidenceRented;
  }

  policyUWR140(): boolean {
    // UWR-140 condition
    // 1. Roof Surfacing Payment Schedule is on the policy AND
    // 2. Agent enters policy change to remove
    // Form-Type: HO3, DP3

    const parentRoofSurfacingPaymentSchedule = this.parentRiskDetail?.riskEndorsements[0].isRoofSurfacingPaymentSchedule;
    const currentRoofSurfacingPaymentSchedule = this.endorsementsData.endorsementsGroup.get('roofSurfacingPaymentSchedule')?.value;

    return parentRoofSurfacingPaymentSchedule !== currentRoofSurfacingPaymentSchedule;
  }
}
